import axios from 'axios';
import { DateTime } from 'luxon';
import { computed, makeObservable } from 'mobx';

import { yandexTrackerApi } from '@/api/yandexTrackerApi';
import { MAX_TIMEOUT_MS } from '@/configs/constants';
import { LoadingStageModel } from '@/models/LoadingStageModel';
import { ValueModel } from '@/models/ValueModel';
import { User, UserRole } from '@/types/user';
import { formatError } from '@/utils/formatError';

import { AlertsStore } from '../AlertsStore';

import { IUserStore } from './types';

export class UserStore implements IUserStore {
  private readonly _user: ValueModel<User | null> = new ValueModel<User | null>(null);

  private readonly loadingStage = new LoadingStageModel();
  private readonly _alertsStore: AlertsStore;
  private fetchUserAbortController: AbortController | null = null;
  private readonly _isYandexTokenExpired = new ValueModel(false);
  private _yandexTokenTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(alertsStore: AlertsStore) {
    this._alertsStore = alertsStore;
    makeObservable(this, {
      user: computed,
      fullName: computed,
      isAdmin: computed,
      isAuthorized: computed,
      hasYandexAccess: computed,
      isLoading: computed,
    });

    this.initializeSession();
  }

  get user() {
    return this._user.value || null;
  }

  get isAdmin() {
    return this.user?.roles.includes(UserRole.admin) || false;
  }

  get fullName() {
    return this.user?.fullName ?? '';
  }

  get isAuthorized() {
    return !!this.user;
  }

  /**
   * Старому учёту нужен токен Яндекс Трекера: бэк хранит его у пользователя, а не в сессии,
   * поэтому после входа токеном он работает и в сессии KTSID — пока не истёк.
   * Срок отслеживает таймер: открытая страница узнаёт о протухании сразу, а не на запросе
   */
  get hasYandexAccess() {
    return (
      !!this.user?.yandexUid &&
      !!this.user.accessTokenExpiresAt &&
      !this._isYandexTokenExpired.value
    );
  }

  get isLoading() {
    return this.loadingStage.isLoading;
  }

  get loadingStageModel() {
    return this.loadingStage;
  }

  private initializeSession = async () => {
    this.loadingStage.loading();

    try {
      await this.fetchMe();
    } catch (e) {
      if (axios.isAxiosError(e) && e.code === 'ERR_CANCELED') {
        console.log('Запрос отменён');
      } else if (axios.isAxiosError(e) && e.response?.status === 401) {
        // Session not valid or expired - this is expected, not an error
        this.resetUser();
      } else {
        console.error('Ошибка при проверке сессии:', e);
        this.resetUser();
      }
    } finally {
      this.loadingStage.success();
    }
  };

  fetchMe = async (): Promise<void> => {
    if (this.fetchUserAbortController) {
      this.fetchUserAbortController.abort();
    }

    this.fetchUserAbortController = new AbortController();
    const signal = this.fetchUserAbortController.signal;

    try {
      const response = await yandexTrackerApi.getMe(signal);
      this.setUser(response.item);
    } finally {
      this.fetchUserAbortController = null;
    }
  };

  login = async (token: string): Promise<void> => {
    this.loadingStage.loading();

    try {
      await yandexTrackerApi.loginByToken(token);
      await this.fetchMe();
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 401) {
        throw new Error('Невалидный токен доступа');
      }

      const error = formatError(e);

      this._alertsStore.addAlert({
        message: error.message,
        title: error.title,
        withReload: false,
        autoClose: true,
      });

      this.loadingStage.error();

      throw e;
    } finally {
      this.loadingStage.success();
    }
  };

  setUser = (user: User) => {
    this._user.change(user);
    this._watchYandexToken();
  };

  logout = async (): Promise<void> => {
    try {
      await yandexTrackerApi.logout();
    } catch {
      // Очищаем состояние даже если запрос не прошёл
    } finally {
      this.resetUser();
    }
  };

  resetUser = () => {
    this._user.reset();
    this._clearYandexTokenTimer();
  };

  private _watchYandexToken = () => {
    this._clearYandexTokenTimer();

    const expiresAt = this.user?.accessTokenExpiresAt;
    const msLeft = expiresAt ? DateTime.fromISO(expiresAt).diffNow().as('milliseconds') : 0;

    this._isYandexTokenExpired.change(msLeft <= 0);

    if (msLeft <= 0) return;

    this._yandexTokenTimer = setTimeout(this._onYandexTokenTimer, Math.min(msLeft, MAX_TIMEOUT_MS));
  };

  // Токен могли обновить входом в другой вкладке — срок спрашиваем у бэка, а не верим старому
  private _onYandexTokenTimer = async () => {
    try {
      await this.fetchMe();
    } catch {
      this._watchYandexToken();
    }
  };

  private _clearYandexTokenTimer = () => {
    if (this._yandexTokenTimer) clearTimeout(this._yandexTokenTimer);

    this._yandexTokenTimer = null;
  };
}
